/*
 * StatusTab group (Figma 119:44931 "tabs" / StatusTab 119:14896) — "Status view switch with
 * editable label and count. Active is the current view."
 * Group: --surface-page fill, padding 2px, radius 8, --shadow-segmented.
 * Tab: height 36, padding 4px 12px, gap 6, radius 6; active fill --surface-default.
 * Label 550 14/20 --text-default. Count: --surface-page fill, padding-x 4px, radius 4,
 * 550 14/20 --text-weak.
 * Narrow screens (not in Figma): the group is never wider than its parent; tabs that don't fit
 * scroll sideways inside it (no scrollbar drawn), and the active tab is scrolled into view. On a
 * touch screen the tabs are --size-touch-target (40px) high.
 * Motion (not in Figma): the active fill is one thumb that slides to the tab chosen (useThumb);
 * a tab not active darkens on hover and press.
 */
import { useLayoutEffect, useRef } from 'react'
import { cx } from './cx'
import { useThumb } from './useThumb'

export interface StatusTabItem<K extends string> {
    key: K
    label: string
    count?: number | string
}

/** Scrolls `container` (positioned: the items' offsetParent) sideways only, so that `item` is in view. */
export function revealInStrip(container: HTMLElement | null, item: HTMLElement | null) {
    if (!container || !item || container.scrollWidth <= container.clientWidth) return
    const left = item.offsetLeft
    const right = left + item.offsetWidth
    if (left < container.scrollLeft) container.scrollLeft = Math.max(0, left - 2)
    else if (right > container.scrollLeft + container.clientWidth) container.scrollLeft = right - container.clientWidth + 2
}

export function StatusTabs<K extends string>({
    items,
    value,
    onChange,
    ariaLabel,
}: {
    items: StatusTabItem<K>[]
    value: K
    onChange: (key: K) => void
    ariaLabel: string
}) {
    const strip = useRef<HTMLDivElement>(null)
    const activeTab = useRef<HTMLButtonElement>(null)
    useLayoutEffect(() => revealInStrip(strip.current, activeTab.current), [value])
    const thumb = useThumb(strip, activeTab, value)
    return (
        <div
            ref={strip}
            role="tablist"
            aria-label={ariaLabel}
            className="scrollbar-none relative flex max-w-full items-start overflow-x-auto overflow-y-clip rounded-[8px] bg-[var(--surface-page)] p-[2px] shadow-[var(--shadow-segmented)]"
        >
            {thumb && <span aria-hidden data-thumb className="thumb absolute top-[2px] bottom-[2px] left-0 rounded-[6px] bg-[var(--surface-default)]" style={thumb} />}
            {items.map((item) => {
                const active = item.key === value
                return (
                    <button
                        key={item.key}
                        ref={active ? activeTab : undefined}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(item.key)}
                        className={cx(
                            'relative flex h-[36px] shrink-0 cursor-pointer items-center justify-center gap-[6px] rounded-[6px] px-[12px] py-[4px]',
                            'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none pointer-coarse:h-[var(--size-touch-target)]',
                            active ? !thumb && 'bg-[var(--surface-default)]' : 'hover:bg-[var(--interaction-hover)] active:bg-[var(--interaction-active)]',
                        )}
                    >
                        <span className="whitespace-nowrap text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">{item.label}</span>
                        {item.count !== undefined && (
                            <span className="flex items-start overflow-clip rounded-[4px] bg-[var(--surface-page)] px-[4px]">
                                <span className="whitespace-nowrap text-[14px] leading-[20px] font-[550] text-[var(--text-weak)]">{item.count}</span>
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
